import { clearLocalAutomationState } from "./clearLocalAutomationState";
import { invalidateCachedDownloadUrlsForUser } from "./firebaseStorageUrl";
import { invalidateUserFileListCache } from "./firebaseStorageFileList";
import { clearLikelyAuthSession } from "./likelyAuthSession";
import { clearRemoveAfterSelectionSessionState } from "./randomizerRemoveAfterSelection";

/**
 * Clears browser storage keys tied to a specific authenticated user.
 * Called on logout / account switch so a different user on the same device
 * cannot read another teacher's drafts, randomizer state, or checkout flags.
 *
 * Does NOT clear Firestore's IndexedDB persistent cache — that is managed by
 * the Firestore SDK and keyed by Firebase project, not app-level user id.
 */

const USER_SCOPED_PREFIXES = [
  "contentBoxDraft:",
  "meta_subscription_tracked:",
  "meta_checkout_",
];

const USER_SCOPED_SUFFIX_PATTERNS = [
  /^useRemoveNames-/,
  /^namesToRemoveList-/,
  /^removeAfterSelectionMode-/,
  /^printView_boxVisibility_/,
];

const SESSION_KEYS_ON_LOGOUT = [
  "authBootstrapPending",
];

const clearStorageKeys = (storage, uid) => {
  if (!storage || !uid) return;

  const keysToRemove = [];

  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key) continue;

    const matchesPrefix = USER_SCOPED_PREFIXES.some(
      (prefix) => key.startsWith(prefix) && key.includes(uid)
    );
    const matchesUidInKey = key.includes(`:${uid}:`) || key.includes(`-${uid}-`) || key.endsWith(`-${uid}`);
    const matchesSuffix = USER_SCOPED_SUFFIX_PATTERNS.some((pattern) => pattern.test(key));

    if (matchesPrefix || (matchesUidInKey && key.includes(uid)) || matchesSuffix) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => {
    try {
      storage.removeItem(key);
    } catch {
      // no-op
    }
  });
};

/**
 * @param {string|null|undefined} uid - Firebase Auth uid being logged out
 */
export const clearUserScopedStorage = (uid) => {
  if (typeof window === "undefined") return;

  if (uid) {
    invalidateCachedDownloadUrlsForUser(uid);
    invalidateUserFileListCache(uid);
    clearStorageKeys(window.localStorage, uid);
    clearStorageKeys(window.sessionStorage, uid);

    try {
      window.sessionStorage.removeItem(`meta_subscription_tracked:${uid}`);
    } catch {
      // no-op
    }
  }

  SESSION_KEYS_ON_LOGOUT.forEach((key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // no-op
    }
  });

  clearLikelyAuthSession();

  try {
    clearLocalAutomationState();
  } catch {
    // no-op
  }

  clearRemoveAfterSelectionSessionState();
};
